import React, { useState, useEffect } from 'react';
import { ArrowRight, Phone, Sparkles, CalendarCheck } from 'lucide-react';
import { Button } from './ui/button';
import { companyInfo } from '../data/mock';
import BookingModal from './BookingModal';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const HeroSection = ({ t, currentLang }) => {
  const [isBookingOpen, setIsBookingOpen] = useState(false);
  const [reviewStats, setReviewStats] = useState({ count: 0, averageRating: 5.0 });

  useEffect(() => {
    const fetchReviewStats = async () => {
      try {
        const response = await fetch(`${API_URL}/api/reviews`);
        if (response.ok) {
          const data = await response.json();
          if (data && data.length > 0) {
            const avg = data.reduce((sum, r) => sum + r.rating, 0) / data.length;
            setReviewStats({
              count: data.length,
              averageRating: Math.round(avg * 10) / 10
            });
          }
        }
      } catch (error) {
        console.log('Using default review stats');
      }
    };

    fetchReviewStats();
  }, []);

  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <section id="home" className="relative min-h-screen flex items-center pt-20 overflow-hidden">
      {/* Background Image with Overlay */}
      <div className="absolute inset-0 z-0">
        <img
          src="https://images.unsplash.com/photo-1686178827149-6d55c72d81df"
          alt="Professional Cleaning"
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-white/95 via-white/90 to-white/70"></div>
      </div>

      {/* Content */}
      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          {/* Left Column - Text Content */}
          <div className="space-y-8">
            {/* Badge */}
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-[#35c6e1]/10 rounded-full border border-[#35c6e1]/20">
              <Sparkles className="w-4 h-4 text-[#2196f3]" />
              <span className="text-sm font-medium text-[#0d2e5d]">
                {t.footer.professional}
              </span>
            </div>

            {/* Main Heading */}
            <div>
              <h1 className="text-5xl sm:text-6xl lg:text-7xl font-bold leading-tight">
                <span className="text-[#0d2e5d]">{t.hero.title}</span>
                <br />
                <span className="text-[#2196f3]">{t.hero.subtitle}</span>
              </h1>
              <p className="mt-6 text-lg text-gray-600 leading-relaxed max-w-xl">
                {t.hero.description}
              </p>
            </div>

            {/* CTAs */}
            <div className="flex flex-col sm:flex-row gap-4">
              <Button
                onClick={() => setIsBookingOpen(true)}
                size="lg"
                className="bg-[#5cd26f] hover:bg-[#4ab860] text-white text-lg px-8 py-6 rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 group"
              >
                <CalendarCheck className="mr-2 w-5 h-5" />
                {currentLang === 'ro' ? 'Solicită Programare' : 'Book Appointment'}
                <ArrowRight className="ml-2 w-5 h-5 group-hover:translate-x-1 transition-transform" />
              </Button>
              <Button
                onClick={() => scrollToSection('contact')}
                size="lg"
                variant="outline"
                className="border-2 border-[#2196f3] text-[#2196f3] hover:bg-[#2196f3] hover:text-white text-lg px-8 py-6 rounded-xl transition-all duration-300"
              >
                {t.hero.ctaPrimary}
              </Button>
            </div>

            {/* Phone Contact */}
            <div className="flex items-center gap-4 pt-4">
              <div className="flex items-center gap-3 px-6 py-3 bg-white rounded-xl shadow-md">
                <div className="p-2 bg-[#5cd26f]/10 rounded-lg">
                  <Phone className="w-5 h-5 text-[#5cd26f]" />
                </div>
                <div>
                  <p className="text-xs text-gray-500 font-medium">{t.common.contactUs}</p>
                  <a
                    href={`tel:${companyInfo.phone}`}
                    className="text-sm font-bold text-[#0d2e5d] hover:text-[#2196f3] transition-colors"
                  >
                    {companyInfo.phone}
                  </a>
                </div>
              </div>
            </div>

            {/* Stats */}
            <div className="grid grid-cols-3 gap-6 pt-8">
              <div className="text-center">
                <div className="text-3xl font-bold text-[#2196f3]">{reviewStats.count}</div>
                <div className="text-sm text-gray-600 mt-1">
                  {t.nav.reviews}
                </div>
              </div>
              <div className="text-center">
                <div className="text-3xl font-bold text-[#2196f3] flex items-center justify-center gap-1">
                  {reviewStats.averageRating}
                  <svg className="w-7 h-7 text-yellow-400" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M12 17.27L18.18 21l-1.64-7.03L22 9.24l-7.19-.61L12 2 9.19 8.63 2 9.24l5.46 4.73L5.82 21z"/>
                  </svg>
                </div>
                <div className="text-sm text-gray-600 mt-1">Rating Google</div>
              </div>
              <div className="text-center">
                <div className="text-3xl font-bold text-[#2196f3]">100%</div>
                <div className="text-sm text-gray-600 mt-1">
                  {currentLang === 'ro' ? 'Satisfacție' : 'Satisfaction'}
                </div>
              </div>
            </div>
          </div>

          {/* Right Column - Image Showcase (Hidden on mobile, visible on larger screens) */}
          <div className="hidden lg:block relative">
            <div className="relative">
              {/* Decorative Elements */}
              <div className="absolute -top-6 -right-6 w-72 h-72 bg-[#35c6e1]/20 rounded-full blur-3xl"></div>
              <div className="absolute -bottom-6 -left-6 w-72 h-72 bg-[#5cd26f]/20 rounded-full blur-3xl"></div>
              
              {/* Main Image */}
              <div className="relative z-10">
                <img
                  src="https://customer-assets.emergentagent.com/job_e82780d4-6c03-49a2-89a1-f21287bc8dc2/artifacts/f1ywd60z_Screenshot_20260311_114948_Gallery.jpg"
                  alt="YmaKOOLat - Curățare profesională canapele"
                  className="rounded-3xl shadow-2xl object-cover"
                />
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Booking Modal */}
      <BookingModal
        isOpen={isBookingOpen}
        onClose={() => setIsBookingOpen(false)}
        t={t}
        currentLang={currentLang}
      />

      {/* Animated Scroll Indicator */}
      <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 z-10">
        <div className="animate-bounce">
          <div className="w-6 h-10 border-2 border-[#2196f3] rounded-full flex items-start justify-center p-2">
            <div className="w-1 h-3 bg-[#2196f3] rounded-full animate-pulse"></div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default HeroSection;
